import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { defaultIfEmpty, exhaustMap, filter, map, switchMap, take } from 'rxjs/operators';
import { Constants } from '../util/constants';
import { AccountApiService } from './account-api.service';

export interface JobPollingOptions {
  /** Intervalo entre consultas al estado del job (ms). */
  intervalMs?: number;
  /** Cantidad máxima de consultas antes de considerar vencida la ventana de espera. */
  maxAttempts?: number;
  /** Mostrar el loader global en cada consulta (default: true, comportamiento previo). */
  showLoader?: boolean;
  /**
   * Permite tratar un 200 como "todavía pendiente" según el body (p. ej. `status: 'PROCESSING'`).
   * Por defecto solo el 204 es pendiente.
   */
  isPending?: (body: any) => boolean;
}

/** Código de error emitido cuando se agota la ventana de espera sin resultado. */
export const PENDING_JOB_TIMEOUT = 'PENDING_JOB_TIMEOUT';

/**
 * Polling genérico sobre `/core/portal/jobs/{jobId}/status`.
 *
 * Contrato del endpoint:
 *  - 204 → el job sigue pendiente.
 *  - 200 → el job terminó; el body es el resultado.
 *
 * Si se agota la ventana (intervalMs * maxAttempts) sin resultado, emite un error con
 * `{ error: { error: 'PENDING_JOB_TIMEOUT', errorMessage } }` (mismo formato que usaba
 * AccountState, para no romper a los consumidores existentes).
 *
 * Nota: el vencimiento de la ventana NO cancela el job en el backend; el análisis sigue
 * en background (PRD CA13).
 */
@Injectable({ providedIn: 'root' })
export class JobPollingService {

  constructor(private accountApiService: AccountApiService) {}

  pollJob<T = any>(jobId: string, options: JobPollingOptions = {}): Observable<T> {
    const intervalMs = options.intervalMs ?? Constants.JOB_POLLING_INTERVAL_MS;
    const maxAttempts = options.maxAttempts ?? Constants.JOB_POLLING_MAX_ATTEMPTS;
    const showLoader = options.showLoader ?? true;

    return timer(0, intervalMs).pipe(
      take(maxAttempts),
      exhaustMap(() => this.accountApiService.getJobStatus(jobId, showLoader)),
      map((httpResponse: HttpResponse<T>) => {
        if (httpResponse.status === 204 || options.isPending?.(httpResponse.body)) {
          return { pending: true, result: null as T };
        }
        return { pending: false, result: httpResponse.body };
      }),
      filter(response => !response.pending),
      take(1),
      map(response => response.result),
      defaultIfEmpty(null as T),
      switchMap(result => {
        if (result !== null) {
          return of(result);
        }

        return throwError({
          error: {
            error: PENDING_JOB_TIMEOUT,
            errorMessage: `El job ${jobId} sigue pendiente luego de ${maxAttempts * intervalMs / 1000} segundos. Volvé a intentar más tarde.`
          }
        });
      })
    );
  }
}
